import styled, { css } from 'styled-components'
import noop from 'lodash/noop'

import { colors } from '@/styles'

type CheckboxProps = Omit<
  React.HTMLProps<HTMLInputElement>,
  'type' | 'checked' | 'value' | 'onChange' | 'as' | 'ref'
>

type Event = React.ChangeEvent<HTMLInputElement>

type State = string | boolean

type Size = 'large' | 'small'

type StyledProps = {
  size?: Size
}

export type SwitchToggleProps = CheckboxProps &
  StyledProps & {
    enabled: State
    value?: string
    onChange?: (e: State) => void
  }

const getSizeStyles = (props: StyledProps) => {
  if (props.size === 'small') return small
  return large
}

const large = css`
  --switch-width: 44px;
  --switch-height: 24px;
  --toggle-width: 20px;
  --toggle-height: 20px;
  --switch-offset: 2px;
`

const small = css`
  --switch-width: 40px;
  --switch-height: 20px;
  --toggle-width: 16px;
  --toggle-height: 16px;
  --switch-offset: 2px;
`

const Input = styled.input<StyledProps>`
  ${props => getSizeStyles(props)};

  -webkit-appearance: initial;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  position: relative;
  background-color: ${colors.grey88};
  width: var(--switch-width);
  height: var(--switch-height);
  border-radius: 12px;
  cursor: pointer;
  transition: background-color 0.22s ease-in-out 0s;

  &:checked {
    background-color: ${colors.blue16};
  }

  &:after {
    position: absolute;
    box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06);
    display: block;
    background: ${colors.white};
    transition: transform 0.22s ease-in-out 0s;
    border-radius: 999px;
    top: var(--switch-offset);
    width: var(--toggle-width);
    height: var(--toggle-height);
    left: 0;
  }

  &:after {
    transform: translateX(var(--switch-offset));
    content: '';
  }

  &:checked:after {
    transform: translateX(
      calc(var(--switch-width) - (var(--switch-offset) + var(--toggle-width)))
    );
    content: '';
  }
`

const SwitchToggle: React.FC<SwitchToggleProps> = ({
  enabled = false,
  value,
  onChange = noop,
  ...rest
}) => {
  const toggleSwitch = (e: Event) => {
    const checked = e.currentTarget.checked
    const state = (() => {
      const hasValue = typeof value === 'string'
      if (hasValue) return checked ? value : ''
      return checked
    })()
    onChange(state)
  }
  return (
    <Input
      type="checkbox"
      checked={!!enabled}
      value={value}
      onChange={toggleSwitch}
      {...rest}
    />
  )
}

export default SwitchToggle
